import { DocumentListItem } from "../DocumentListItem";
import { BookmarkModel } from "client/Api/Documents/Bookmarks/bookmarks";
import {
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useImperativeHandle,
	useRef
} from "react";
import { SelectableItemOptions } from "client/Hooks";
import { BookmarkAction, BookmarkActionContext } from "./bookmarkActions";
import { ScrollableVirtualList, ScrollableVirtualListHandle } from "client/Components";
import { DocumentNoResult } from "../DocumentNoResult";

export type BookmarksListViewHandle = {
	resetScrollPosition: () => void;
};

export type BookmarksListViewProps = {
	isBusy: boolean;
	selectionFor: (item: BookmarkModel) => SelectableItemOptions;
	items: readonly BookmarkModel[];
	isInSelectMode: boolean;
	onEnableSelectMode: () => void;
	onOpenItem: (bookmark: BookmarkModel) => void;
	selectionActions: BookmarkAction[];
	actionContext: BookmarkActionContext;
	hasSearch: boolean;
};

export const BookmarksListView = forwardRef(
	(props: BookmarksListViewProps, ref: Ref<BookmarksListViewHandle>): ReactElement => {
		const {
			isBusy,
			items,
			isInSelectMode,
			selectionFor,
			actionContext,
			selectionActions,
			onEnableSelectMode,
			onOpenItem,
			hasSearch
		} = props;

		const listHandle: RefObject<ScrollableVirtualListHandle<BookmarkModel> | null> = useRef<ScrollableVirtualListHandle<BookmarkModel>>(null);

		useImperativeHandle(
			ref,
			(): BookmarksListViewHandle => ({
				resetScrollPosition: () => {
					listHandle.current
						?.scrollToTop();
				}
			}),
			[]
		);

		if (!isBusy && items.length === 0) {
			return (
				<DocumentNoResult
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<ScrollableVirtualList
				items={items}
				estimatedHeightOfAnItem={64}
				itemKey={getUniqueKey}
				ref={listHandle}
				renderAfterItems={
					<ScrollableVirtualList.LoadMoreIndicator
						isLoadingData={isBusy}
						hasMoreItems={false}
						onRequestMoreItems={() => { /* NOOP */ }}
					/>
				}
				trackingId="bookmark-list-view"
			>
				{(item: BookmarkModel): ReactElement => (
					<DocumentListItem
						name={item.name}
						documentKind={item.itemType}
						extension={item.extension}
						isInSelectMode={isInSelectMode}
						selectable={selectionFor(item)}
						onEnableSelectMode={onEnableSelectMode}
						onOpenItem={() => onOpenItem(item)}
						actions={selectionActions}
						actionContext={{ ...actionContext, selection: [item] }}
					/>
				)}
			</ScrollableVirtualList>
		);
	}
);

BookmarksListView.displayName = "BookmarksListView";

function getUniqueKey(item: BookmarkModel): string {
	return `${item.id}`;
}
